import { useEffect, useRef } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPreferences } from "features/map/control/selectors"
import {
    initWeatherPreferences,
    setWeatherLightningUnit,
    setWeatherPrecipUnit,
    setWeatherWindUnit,
} from "features/map/control/slice"
import { LightningUnit, PrecipUnit, WeatherPreferences, WindUnit } from "features/map/control/types"
import {
    selectActiveWorkspace,
    selectWorkspaceUnitSystem,
} from "features/data-management/workspace/WorkspaceManagementSlice"

const STORAGE_KEY = "weatherPreferences"

const DEFAULTS: Record<"metric" | "imperial", WeatherPreferences> = {
    metric: {
        windUnit: WindUnit.KilometersPerHour,
        precipUnit: PrecipUnit.Millimeters,
        lightningUnit: LightningUnit.PerSquareKilometerHour,
    },
    imperial: {
        windUnit: WindUnit.MilesPerHour,
        precipUnit: PrecipUnit.Inches,
        lightningUnit: LightningUnit.PerSquareMileHour,
    },
}

const isEnumValue = <T extends Record<string, string>>(
    enumObject: T,
    value: unknown
): value is T[keyof T] => Object.values(enumObject).includes(value as T[keyof T])

const readStoredPreferences = (): Partial<WeatherPreferences> => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed: unknown = JSON.parse(raw)
        if (typeof parsed !== "object" || parsed === null) return {}
        const { windUnit, precipUnit, lightningUnit } = parsed as Record<string, unknown>
        const stored: Partial<WeatherPreferences> = {}
        if (isEnumValue(WindUnit, windUnit)) stored.windUnit = windUnit
        if (isEnumValue(PrecipUnit, precipUnit)) stored.precipUnit = precipUnit
        if (isEnumValue(LightningUnit, lightningUnit)) stored.lightningUnit = lightningUnit
        return stored
    } catch {
        return {}
    }
}

export const useWeatherPreferences = () => {
    const dispatch = useAppDispatch()
    const preferences = useAppSelector(selectWeatherPreferences)
    const unitSystem = useAppSelector(selectWorkspaceUnitSystem)
    const workspace = useAppSelector(selectActiveWorkspace())
    const initialized = useRef(false)

    useEffect(() => {
        if (initialized.current || !workspace) return
        initialized.current = true
        const resolved: WeatherPreferences = { ...DEFAULTS[unitSystem], ...readStoredPreferences() }
        dispatch(initWeatherPreferences(resolved))
    }, [dispatch, workspace, unitSystem])

    useEffect(() => {
        if (!initialized.current) return
    }, [preferences])

    return {
        ...preferences,
        setWindUnit: (value: WindUnit) => dispatch(setWeatherWindUnit(value)),
        setPrecipUnit: (value: PrecipUnit) => dispatch(setWeatherPrecipUnit(value)),
        setLightningUnit: (value: LightningUnit) => dispatch(setWeatherLightningUnit(value)),
    }
}
